import { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useMeasure } from '@core/hooks/useMeasure';
import { useContextTranslation } from '@core/i18n';
import { useContextParams } from '@core/params/context';
import { NEXT_PRODUCT_CARD_BUTTON, PREVIOUS_PRODUCT_CARD_BUTTON, PRODUCT_CARD_CAROUSEL, } from '@player/testIds';
import { NavButton } from './NavButton';
import { CARD_HEIGHT, CARD_MAX_WIDTH, CARD_MIN_WIDTH, CardsWrapper, CardWrapper, Wrapper, } from './ProductCardCarousel.styles';
import { ProductCardItem } from './ProductCardItem';
/**
 * Reusable product card carousel component.
 * Note yet implemented:
 *  - deals if applicable
 *  - sold out products (from livestream)
 */
export const ProductCardCarousel = ({ firstCardCentered, cardSizeOffset, compactSize, onCardClick, onCardVisibilityChange, padding, products, showCTA, showNavButtons, }) => {
    const params = useContextParams();
    const isRTL = params?.dir === 'rtl';
    const { t } = useContextTranslation();
    const [visibleCardIndex, setVisibleCardIndex] = useState(Array(products.length).fill(false));
    const { ref: cardLayoutContainer, rect: { width }, } = useMeasure();
    const cardSize = useMemo(() => compactSize
        ? CARD_HEIGHT
        : Math.max(CARD_MIN_WIDTH, Math.min(CARD_MAX_WIDTH, width ?? 0)) -
            (cardSizeOffset || 0), [cardSizeOffset, compactSize, width]);
    const allCardsVisible = (width || 0) > cardSize * products.length;
    const onPrevClick = useCallback(() => {
        cardLayoutContainer.current?.scrollBy(-cardSize, 0);
    }, [cardLayoutContainer, cardSize]);
    const onNextClick = useCallback(() => {
        cardLayoutContainer.current?.scrollBy(cardSize, 0);
    }, [cardLayoutContainer, cardSize]);
    const visibilityCallbacks = useMemo(() => {
        return products.map((product, index) => {
            return (isVisible) => {
                onCardVisibilityChange?.(isVisible, product);
                setVisibleCardIndex((state) => [
                    ...state.slice(0, index),
                    isVisible,
                    ...state.slice(index + 1),
                ]);
            };
        });
    }, [onCardVisibilityChange, products]);
    const clickCallbacks = useMemo(() => {
        return products.map((product) => {
            return (event) => {
                onCardClick?.(event, product);
                event.stopPropagation(); // prevent click event trigger for parent layers
            };
        });
    }, [onCardClick, products]);
    return products?.length ? (<Wrapper>
      {/* If its just one product we don't show the nav buttons */}
      {Boolean(showNavButtons) && !allCardsVisible && products.length > 1 ? (<>
          {!visibleCardIndex[0] ? (<NavButton data-testid={PREVIOUS_PRODUCT_CARD_BUTTON} css={css `
                padding: 10px;
                position: absolute;
                ${isRTL ? 'right: 0;' : 'left: 0;'}
                top: 50%;
                transform: translateY(-50%);
                height: 100%;
              `} onClick={isRTL ? onNextClick : onPrevClick} title={t('player:Previous Card')}/>) : null}
          {!visibleCardIndex[products.length - 1] ? (<NavButton data-testid={NEXT_PRODUCT_CARD_BUTTON} css={css `
                padding: 10px;
                position: absolute;
                top: 50%;
                ${isRTL ? 'left: 0;' : 'right: 0;'}
                transform: translateY(-50%);
                height: 100%;
              `} isRight={true} onClick={isRTL ? onPrevClick : onNextClick} title={t('player:Next Card')}/>) : null}
        </>) : null}
      <CardsWrapper allCardsCentered={allCardsVisible} cardSize={cardSize} data-testid={PRODUCT_CARD_CAROUSEL} firstCardCentered={!!firstCardCentered} padding={padding} ref={cardLayoutContainer}>
        {products.map((product, index) => (<CardWrapper key={product.id}>
            <ProductCardItem cardSize={cardSize} compactSize={compactSize} onClick={clickCallbacks[index]} onVisibilityChange={visibilityCallbacks[index]} product={product} showCTA={showCTA}/>
          </CardWrapper>))}
      </CardsWrapper>
    </Wrapper>) : null;
};
